<template>
  <div class="filter-menu" @click.stop>
    <button
      type="button"
      class="sw-chip"
      :class="{ 'is-active': open || activeCount > 0 }"
      @click="toggleMenu"
    >
      <i class="bi bi-funnel"></i>
      Filtros
      <span v-if="activeCount" class="sw-count">{{ activeCount }}</span>
    </button>
    <div v-show="open" class="filter-menu__panel">
      <div class="filter-menu__field">
        <span>Status</span>
        <ScheduleFilterSelect
          :model-value="status"
          :options="statusList"
          option-label="name"
          option-value="code"
          placeholder="Status"
          searchable
          multiple
          :open="openSelect === 'status'"
          @update:open="setOpenSelect('status', $event)"
          @update:modelValue="$emit('update:status', $event)"
        />
      </div>
      <div class="filter-menu__field">
        <span>Especialidade</span>
        <ScheduleFilterSelect
          :model-value="idSpec"
          :options="specialties"
          option-label="spec_name"
          option-value="id_spec"
          placeholder="Especialidades"
          searchable
          show-clear
          :open="openSelect === 'spec'"
          @update:open="setOpenSelect('spec', $event)"
          @update:modelValue="$emit('update:idSpec', $event)"
          @change="$emit('change-spec', $event)"
        />
      </div>
      <div class="filter-menu__field">
        <span>Forma de pagamento</span>
        <ScheduleFilterSelect
          :model-value="idPaymeth"
          :options="paymentMethods"
          option-label="paymeth_name"
          option-value="id_paymeth"
          placeholder="Forma de pagamento"
          searchable
          show-clear
          :open="openSelect === 'paymeth'"
          @update:open="setOpenSelect('paymeth', $event)"
          @update:modelValue="$emit('update:idPaymeth', $event)"
        />
      </div>
      <div class="filter-menu__field">
        <span>Convênio</span>
        <ScheduleFilterSelect
          :model-value="idInsurance"
          :options="insurances"
          option-label="label"
          option-value="id_insurance"
          placeholder="Convênio"
          searchable
          show-clear
          :filter-fields="['label', 'insurance_name', 'trading_name']"
          :open="openSelect === 'insurance'"
          @update:open="setOpenSelect('insurance', $event)"
          @update:modelValue="$emit('update:idInsurance', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import http from '../../../helpers/http';
import ScheduleFilterSelect from './ScheduleFilterSelect.vue';

const DEFAULT_STATUS = 'AG,AR,IA,CO,FI';

export default {
  name: 'ScheduleFiltersMenu',
  components: { ScheduleFilterSelect },
  props: {
    status: { default: null },
    idProf: { default: null },
    idSpec: { default: null },
    idPaymeth: { default: null },
    idInsurance: { default: null },
  },
  emits: [
    'update:status',
    'update:idProf',
    'update:idSpec',
    'update:idPaymeth',
    'update:idInsurance',
    'change-prof',
    'change-spec',
    'spec-interval',
  ],
  data() {
    return {
      open: false,
      openSelect: null,
      statusList: [],
      specialties: [],
      paymentMethods: [],
      insurances: [],
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    professionals() {
      return this.$store.state.auth.professionals || [];
    },
    activeCount() {
      return [
        this.idSpec,
        this.idPaymeth,
        this.idInsurance,
        this.status && this.status !== DEFAULT_STATUS ? this.status : null,
      ].filter(Boolean).length;
    },
  },
  watch: {
    idProf() {
      this.loadSpecialties();
    },
    idSpec() {
      this.emitSpecInterval();
    },
  },
  mounted() {
    document.addEventListener('click', this.close);
    this.loadStatus();
    this.loadSpecialties();
    this.loadPaymentMethods();
    this.loadInsurances();
    this.ensureDefaultProf();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.close);
  },
  methods: {
    setOpenSelect(name, isOpen) {
      this.openSelect = isOpen ? name : (this.openSelect === name ? null : this.openSelect);
    },
    toggleMenu() {
      this.open = !this.open;
      if (!this.open) this.openSelect = null;
    },
    close(event) {
      const target = event?.target;
      if (target?.closest?.('.ubbi-select__panel, .filter-menu')) return;
      this.open = false;
      this.openSelect = null;
    },
    ensureDefaultProf() {
      if (this.idProf || !this.userAuth?.id_prof) return;
      const professional = this.professionals.find((item) => item.id_prof === this.userAuth.id_prof);
      this.$emit('update:idProf', this.userAuth.id_prof);
      if (professional) this.$emit('change-prof', professional);
    },
    loadStatus() {
      return http.get('/status').then((response) => {
        this.statusList = response.data || [];
        if (!this.status) this.$emit('update:status', DEFAULT_STATUS);
      });
    },
    emitSpecInterval() {
      const spec = this.specialties.find((item) => String(item.id_spec) === String(this.idSpec));
      const minutes = Number(spec?.interval_minutes);
      this.$emit('spec-interval', Number.isFinite(minutes) && minutes > 0 ? minutes : null);
    },
    loadSpecialties() {
      if (!this.place?.id_place) return;
      const url = this.idProf
        ? `/places/${this.place.id_place}/professionals/${this.idProf}/specialties`
        : `/places/${this.place.id_place}/specialties`;
      return http.get(url).then((response) => {
        this.specialties = response.data || [];
        this.emitSpecInterval();
      }).catch(() => {
        this.specialties = [];
        this.emitSpecInterval();
      });
    },
    loadPaymentMethods() {
      if (!this.place?.id_place) return;
      return http.get(`/places/${this.place.id_place}/payment-methods`).then((response) => {
        this.paymentMethods = response.data || [];
      }).catch(() => {
        this.paymentMethods = [];
      });
    },
    loadInsurances() {
      if (!this.place?.id_place) return;
      return http.get(`/places/${this.place.id_place}/insurances`).then((response) => {
        this.insurances = (response.data || []).map((item) => ({
          ...item,
          label: item.trading_name
            ? `${item.trading_name} - ${item.insurance_name}`
            : item.insurance_name,
        }));
      }).catch(() => {
        this.insurances = [];
      });
    },
  },
};
</script>

<style scoped>
.filter-menu {
  position: relative;
}
.filter-menu__panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  z-index: 1060;
  width: 300px;
  padding: 0.85rem;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  box-shadow: 0 12px 32px rgba(24, 28, 50, 0.12);
  display: grid;
  gap: 0.7rem;
  overflow: hidden;
}
.filter-menu__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #7e8299;
  margin: 0;
}
</style>
